import React from "react";
import { MediaStage } from "@/core/types/mediaTypes";
import { MEDIA_STAGE_CONFIG } from "@/core/MediaItem/mediaStageConfig";

interface StageActionsProps {
  onStageChange: (stage: MediaStage) => void;
  currentStage?: MediaStage;
  disabled?: boolean;
  showLeaderHint?: boolean;
}

export const LEADER_KEY = ",";

// Keyboard shortcuts live here (not in the central stage config) because they're
// specific to this review-modal keyboard UX; the label/color come from the
// shared MEDIA_STAGE_CONFIG so translations stay in one place.
const STAGE_BUTTONS = [
  { stage: MediaStage.DRAFT, label: MEDIA_STAGE_CONFIG[MediaStage.DRAFT].label, key: "D" },
  { stage: MediaStage.PENDING_APPROVAL, label: MEDIA_STAGE_CONFIG[MediaStage.PENDING_APPROVAL].label, key: "P" },
  { stage: MediaStage.APPROVED, label: MEDIA_STAGE_CONFIG[MediaStage.APPROVED].label, key: "A" },
  { stage: MediaStage.LEGACY_USED, label: MEDIA_STAGE_CONFIG[MediaStage.LEGACY_USED].label, key: "L" },
  { stage: MediaStage.DISCARDED, label: MEDIA_STAGE_CONFIG[MediaStage.DISCARDED].label, key: "X" },
  { stage: MediaStage.ARCHIVED, label: MEDIA_STAGE_CONFIG[MediaStage.ARCHIVED].label, key: "E" },
] as const;

export const StageActions: React.FC<StageActionsProps> = ({
  onStageChange,
  currentStage,
  disabled = false,
  showLeaderHint = true,
}) => {
  return (
    <>
      <div className="flex items-center justify-between text-gray-300 pt-3 border-t border-gray-700">
        <span className="text-white/70">Stage</span>
        {showLeaderHint && (
          <span className="text-gray-500 text-xs">
            Press <kbd className="px-1 py-0.5 bg-gray-800 rounded-sm">{LEADER_KEY}</kbd> first
          </span>
        )}
      </div>
      {STAGE_BUTTONS.map(({ stage, label, key }) => {
        const isActive = stage === currentStage;
        return (
          <button
            key={stage}
            onClick={() => onStageChange(stage)}
            disabled={disabled}
            className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isActive
                ? "bg-cyan-900/50 text-cyan-300 ring-1 ring-cyan-700"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
            }`}
          >
            <span>{label}</span>
            <kbd className="px-2 py-1 bg-gray-900 rounded-sm text-white text-xs">{LEADER_KEY} {key}</kbd>
          </button>
        );
      })}
    </>
  );
};

export const stageKeyboardConfig: Record<string, MediaStage> = {
  d: MediaStage.DRAFT,
  p: MediaStage.PENDING_APPROVAL,
  a: MediaStage.APPROVED,
  l: MediaStage.LEGACY_USED,
  x: MediaStage.DISCARDED,
  e: MediaStage.ARCHIVED,
};
